import { Injectable } from "@angular/core";
import { AppConfig } from "../configs/app";

const DEFAULT_GTM_ID = "GTM-TLCR2K22";
/* 旧 GTM コンテナと push を共有しないよう専用の配列を使う */
const DATA_LAYER_NAME = "dataLayerGa4";
const SNAPSHOT_KEY = "ga4_snapshot";
const PURCHASE_SENT_KEY_PREFIX = "ga4_purchase_";

const PAYMENT_TYPE_NAMES = {
  1: "credit_card",
  2: "convenience_store",
  4: "postpay",
  5: "paypay",
  6: "paidy",
  7: "gmo_convenience_store",
  8: "gmo_credit_card",
};

const MASKED_PATHS = [
  /^(\/mypages\/reservation\/(?:change\/|confirm\/|webticket\/|emails\/)?)[^\/?#]+/,
  /^(\/info\/refund\/update\/)[^\/?#]+/,
];

const ALLOWED_QUERY_PARAMS = {
  utm_source: /^[A-Za-z0-9\-_.]+$/,
  utm_medium: /^[A-Za-z0-9\-_.]+$/,
  utm_campaign: /^[A-Za-z0-9\-_.]+$/,
  utm_term: /^[A-Za-z0-9\-_.]+$/,
  utm_content: /^[A-Za-z0-9\-_.]+$/,
  gclid: /^[A-Za-z0-9\-_]+$/,
  acd: /^[A-Za-z0-9]+$/,
  dpt_coty_id: /^\d+$/,
  dst_coty_id: /^\d+$/,
  dpt_town_id: /^\d+$/,
  dst_town_id: /^\d+$/,
  dpt_ymd: /^\d{8}$/,
};

@Injectable({
  providedIn: "root",
})
export class AnalyticsService {
  private initialized = false;
  private lastPageLocation: string = null;

  constructor(private appConfig: AppConfig) {}

  init() {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    try {
      const configuredId = (this.appConfig as AppConfig & { gtmId?: string })
        .gtmId;
      const gtmId = configuredId === undefined ? DEFAULT_GTM_ID : configuredId;
      if (!gtmId) {
        return;
      }

      this.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });

      const script = document.createElement("script");
      script.async = true;
      script.src =
        "https://www.googletagmanager.com/gtm.js?id=" +
        encodeURIComponent(gtmId) +
        "&l=" +
        DATA_LAYER_NAME;
      document.head.appendChild(script);
    } catch (e) {}
  }

  /* PAGE VIEW */
  pageView(url: string) {
    try {
      const origin = window.location.origin;
      const pagePath = this.maskUrl(url);
      const pageLocation = origin + pagePath;
      const pageReferrer =
        this.lastPageLocation !== null
          ? this.lastPageLocation
          : this.maskReferrer(document.referrer);
      this.lastPageLocation = pageLocation;

      this.push({
        event: "page_view",
        page_location: pageLocation,
        page_referrer: pageReferrer,
        page_path: pagePath.split("?")[0],
      });
    } catch (e) {}
  }

  /* SEARCH */
  search(searchFrom: string, searchParams: any, filterData: any) {
    try {
      const divisionData =
        filterData && filterData.divisionData
          ? filterData.divisionData
          : this.cachedDivisionData();
      const townData =
        filterData && filterData.townData ? filterData.townData : {};
      const dptCoty = this.cotySlug(divisionData, searchParams.departureCityID);
      const dstCoty = this.cotySlug(
        divisionData,
        searchParams.destinationCityID
      );

      this.push({
        event: "search",
        search_term: dptCoty + ">" + dstCoty,
        search_from: searchFrom,
        dpt_coty_id: this.toId(searchParams.departureCityID),
        dpt_coty: dptCoty,
        dst_coty_id: this.toId(searchParams.destinationCityID),
        dst_coty: dstCoty,
        dpt_town_id: this.toId(searchParams.departureTownID),
        dpt_town: this.townSlug(
          townData.departure,
          searchParams.departureTownID
        ),
        dst_town_id: this.toId(searchParams.destinationTownID),
        dst_town: this.townSlug(
          townData.destination,
          searchParams.destinationTownID
        ),
        dpt_ymd: this.toYmd(searchParams.departureYMD),
      });
    } catch (e) {}
  }

  /* FUNNEL */
  addToCart(cartItem: any, divisionData: any = null) {
    try {
      const legs = this.buildLegs([cartItem], divisionData);
      this.pushEcommerce("add_to_cart", {}, this.ecommerceFromLegs(legs));
    } catch (e) {}
  }

  beginCheckout(cartData: any, loggedIn: boolean) {
    try {
      const legs = this.buildLegs(cartData, null);
      this.pushEcommerce(
        "begin_checkout",
        { logged_in: Boolean(loggedIn) },
        this.ecommerceFromLegs(legs)
      );
    } catch (e) {}
  }

  addPaymentInfo(postData: any, cartData: any) {
    try {
      const legs = this.buildLegs(cartData, null);
      this.pushEcommerce(
        "add_payment_info",
        {
          payment_type: this.paymentTypeName(postData.payment_method),
          payment_method: Number(postData.payment_method),
          has_coupon: Boolean(postData.use_coupon),
        },
        this.ecommerceFromLegs(legs)
      );
    } catch (e) {}
  }

  confirmReservation(
    cartData: any,
    postData: any,
    priceCalculation: any,
    divisionData: any
  ) {
    try {
      const legs = this.buildLegs(cartData, divisionData);
      const snapshot = {
        booking_total: Number(priceCalculation.totalCartPrice),
        fare_total: this.sum(legs, "price"),
        payment_method: Number(postData.payment_method),
        has_coupon: Boolean(postData.use_coupon),
        passengers: this.sum(legs, "adult") + this.sum(legs, "children"),
        legs: legs,
      };
      this.saveSnapshot(snapshot);

      this.push({
        event: "confirm_reservation",
        payment_type: this.paymentTypeName(snapshot.payment_method),
        payment_method: snapshot.payment_method,
        booking_total: snapshot.booking_total,
        has_coupon: snapshot.has_coupon,
      });
    } catch (e) {}
  }

  /* PURCHASE */
  async purchase(finishData: any) {
    try {
      const list =
        finishData &&
        finishData.status == "success" &&
        finishData.data &&
        Array.isArray(finishData.data.reservation_list)
          ? finishData.data.reservation_list
          : [];
      if (list.length === 0) {
        return;
      }
      const paymentMethod = Number(finishData.data.payment_method);

      const transactionId = await this.transactionId(
        list.map((item) => item.reservation_id)
      );
      if (!transactionId) {
        this.pushPurchaseUnmatched("no_id", null, paymentMethod, list.length);
        this.clearSnapshot();
        return;
      }
      if (this.isPurchaseSent(transactionId)) {
        return;
      }

      const snapshot = this.readSnapshot();
      if (!snapshot || !Array.isArray(snapshot.legs)) {
        this.pushPurchaseUnmatched(
          "missing",
          transactionId,
          paymentMethod,
          list.length
        );
      } else if (!this.legsMatch(snapshot.legs, list)) {
        this.pushPurchaseUnmatched(
          "mismatch",
          transactionId,
          paymentMethod,
          list.length
        );
      } else {
        const ecommerce = this.ecommerceFromLegs(snapshot.legs);
        ecommerce.transaction_id = transactionId;
        ecommerce.value = snapshot.booking_total;
        this.pushEcommerce(
          "purchase",
          {
            payment_type: this.paymentTypeName(snapshot.payment_method),
            payment_method: snapshot.payment_method,
            passengers: snapshot.passengers,
            fare_total: snapshot.fare_total,
            dpt_ymd: snapshot.legs[0].dpt_ymd,
            legs: snapshot.legs.length,
            has_coupon: snapshot.has_coupon,
          },
          ecommerce
        );
      }

      this.markPurchaseSent(transactionId);
      this.clearSnapshot();
    } catch (e) {}
  }

  async purchaseFallback(serverFallback: any) {
    try {
      const ids =
        serverFallback && Array.isArray(serverFallback.ids)
          ? serverFallback.ids
          : [];
      const transactionId = await this.transactionId(ids);
      if (transactionId && this.isPurchaseSent(transactionId)) {
        return;
      }

      const snapshot = this.readSnapshot();
      this.pushPurchaseUnmatched(
        "fallback",
        transactionId,
        snapshot ? Number(snapshot.payment_method) : null,
        ids.length
      );

      if (transactionId) {
        this.markPurchaseSent(transactionId);
      }
      this.clearSnapshot();
    } catch (e) {}
  }

  /* HELPERS */
  push(data: any) {
    try {
      const w: any = window;
      w[DATA_LAYER_NAME] = w[DATA_LAYER_NAME] || [];
      w[DATA_LAYER_NAME].push(data);
    } catch (e) {}
  }

  pushEcommerce(event: string, params: any, ecommerce: any) {
    this.push({ ecommerce: null });
    this.push(
      Object.assign({ event: event }, params, { ecommerce: ecommerce })
    );
  }

  maskUrl(url: string) {
    let path = String(url || "/").split("#")[0];
    let query = "";
    const queryIndex = path.indexOf("?");
    if (queryIndex !== -1) {
      query = path.substring(queryIndex + 1);
      path = path.substring(0, queryIndex);
    }

    for (const pattern of MASKED_PATHS) {
      path = path.replace(pattern, "$1:id");
    }

    const kept = [];
    for (const pair of query.split("&")) {
      if (!pair) {
        continue;
      }
      const eqIndex = pair.indexOf("=");
      const key = this.decode(
        eqIndex === -1 ? pair : pair.substring(0, eqIndex)
      );
      const value = this.decode(
        eqIndex === -1 ? "" : pair.substring(eqIndex + 1)
      );
      const rule: RegExp = ALLOWED_QUERY_PARAMS[key];
      if (rule && rule.test(value)) {
        kept.push(key + "=" + encodeURIComponent(value));
      }
    }

    return kept.length > 0 ? path + "?" + kept.join("&") : path;
  }

  maskReferrer(referrer: string) {
    if (!referrer) {
      return "";
    }
    const match = /^(https?:\/\/[^\/?#]+)(.*)$/.exec(referrer);
    if (!match) {
      return "";
    }
    if (match[1] === window.location.origin) {
      return match[1] + this.maskUrl(match[2] || "/");
    }
    return match[1];
  }

  cotySlug(divisionData: any, cotyId: any) {
    try {
      const item = divisionData.division_map.id_bb[cotyId];
      return item && item.slug ? String(item.slug) : "";
    } catch (e) {
      return "";
    }
  }

  townSlug(townMapData: any, townId: any) {
    try {
      const item = townMapData.map.id_bb[townId];
      return item && item.slug ? String(item.slug) : "";
    } catch (e) {
      return "";
    }
  }

  paymentTypeName(paymentMethod: any) {
    const name = PAYMENT_TYPE_NAMES[Number(paymentMethod)];
    return name ? name : "";
  }

  /* TRANSACTION ID */
  async transactionId(reservationIds: any[]) {
    try {
      const subtle =
        window.crypto && window.crypto.subtle ? window.crypto.subtle : null;
      if (!subtle || !reservationIds || reservationIds.length === 0) {
        return null;
      }
      const ids = reservationIds.map((id) => Number(id));
      for (const id of ids) {
        if (!id) {
          return null;
        }
      }
      const joined = ids
        .sort((a, b) => a - b)
        .map((id) => String(id))
        .join("-");
      const bytes = new Uint8Array(joined.length);
      for (let i = 0; i < joined.length; i++) {
        bytes[i] = joined.charCodeAt(i);
      }
      const digest = await subtle.digest("SHA-256", bytes);
      let hex = "";
      for (const b of Array.from(new Uint8Array(digest))) {
        hex += ("0" + b.toString(16)).slice(-2);
      }
      return hex.substring(0, 16);
    } catch (e) {
      return null;
    }
  }

  /* SNAPSHOT */
  saveSnapshot(snapshot: any) {
    try {
      window.sessionStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
    } catch (e) {}
  }

  readSnapshot() {
    try {
      const data = window.sessionStorage.getItem(SNAPSHOT_KEY);
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  clearSnapshot() {
    try {
      window.sessionStorage.removeItem(SNAPSHOT_KEY);
    } catch (e) {}
  }

  /* PURCHASE DEDUPLICATION */
  isPurchaseSent(transactionId: string) {
    try {
      const key = PURCHASE_SENT_KEY_PREFIX + transactionId;
      return window.sessionStorage.getItem(key) !== null;
    } catch (e) {
      return false;
    }
  }

  markPurchaseSent(transactionId: string) {
    try {
      const key = PURCHASE_SENT_KEY_PREFIX + transactionId;
      window.sessionStorage.setItem(key, "1");
    } catch (e) {}
  }

  private pushPurchaseUnmatched(
    reason: string,
    transactionId: string,
    paymentMethod: number,
    legs: number
  ) {
    const params: any = {
      event: "purchase_unmatched",
      reason: reason,
      legs: legs,
    };
    if (transactionId) {
      params.transaction_id = transactionId;
    }
    if (paymentMethod) {
      params.payment_type = this.paymentTypeName(paymentMethod);
      params.payment_method = paymentMethod;
    }
    this.push(params);
  }

  private buildLegs(cartData: any, divisionData: any) {
    const division = divisionData ? divisionData : this.cachedDivisionData();
    const legs = [];
    for (const cartItem of Array.isArray(cartData) ? cartData : []) {
      const plan = cartItem.plan || {};
      const stations = Array.isArray(plan.station_list)
        ? plan.station_list
        : [];
      const dptStation = stations[cartItem.departure] || {};
      const dstStation = stations[cartItem.destination] || {};
      const adult = cartItem.passenger
        ? Number(cartItem.passenger.adult.total)
        : 0;
      const children = cartItem.passenger
        ? Number(cartItem.passenger.children.total)
        : 0;
      legs.push({
        plan_code: String(plan.plan_code),
        dpt_ymd: this.toYmd(cartItem.targetDay),
        bus_code: plan.bus_code,
        course_name: plan.course_name,
        company_name: plan.company_name,
        dpt_coty_id: this.toId(dptStation.coty_id),
        dst_coty_id: this.toId(dstStation.coty_id),
        dpt_coty: this.cotySlug(division, dptStation.coty_id),
        dst_coty: this.cotySlug(division, dstStation.coty_id),
        dpt_town_id: this.toId(dptStation.town_id),
        dst_town_id: this.toId(dstStation.town_id),
        price: Number(cartItem.totalPrice) || 0,
        adult: adult || 0,
        children: children || 0,
      });
    }
    return legs;
  }

  private ecommerceFromLegs(legs: any[]): any {
    return {
      currency: "JPY",
      value: this.sum(legs, "price"),
      items: legs.map((leg) => ({
        item_id: leg.bus_code,
        item_name: leg.course_name,
        item_brand: leg.company_name,
        item_category: leg.dpt_coty,
        item_category2: leg.dst_coty,
        item_variant: leg.plan_code,
        price: leg.price,
        quantity: 1,
        passengers: leg.adult + leg.children,
        adult: leg.adult,
        children: leg.children,
        dpt_ymd: leg.dpt_ymd,
      })),
    };
  }

  private legsMatch(legs: any[], reservationList: any[]) {
    if (legs.length !== reservationList.length) {
      return false;
    }
    const key = (planCode: any, ymd: any) =>
      String(planCode) + "|" + this.toYmd(ymd);
    const expected = legs.map((leg) => key(leg.plan_code, leg.dpt_ymd)).sort();
    const actual = reservationList
      .map((item) => key(item.plan_code, item.dpt_ymd))
      .sort();
    return expected.every((value, i) => value === actual[i]);
  }

  private cachedDivisionData() {
    try {
      const data = window.localStorage.getItem(
        this.appConfig.localStorageCachePrefixName +
          this.appConfig.plan.divisionDataSessionName
      );
      return data ? JSON.parse(data) : null;
    } catch (e) {
      return null;
    }
  }

  private sum(legs: any[], field: string) {
    return legs.reduce((total, leg) => total + (Number(leg[field]) || 0), 0);
  }

  private toId(value: any) {
    const valid =
      value !== undefined &&
      value !== null &&
      value !== "" &&
      !isNaN(Number(value));
    return valid ? Number(value) : undefined;
  }

  private toYmd(value: any) {
    const digits = String(value === undefined || value === null ? "" : value)
      .replace(/\D/g, "")
      .substring(0, 8);
    return digits.length === 8 ? digits : "";
  }

  private decode(value: string) {
    try {
      return decodeURIComponent(value.replace(/\+/g, " "));
    } catch (e) {
      return "";
    }
  }
}
